import Hero from '../components/Hero';

const About = () => {
  const values = [
    {
      title: 'Evidence Integrity',
      description: 'Honest about what science says and what it doesn’t say. No commercial pressure changes our evidence assessments.'
    },
    {
      title: 'Wisdom Respect',
      description: 'Contemplative traditions are honored on their own terms, not reduced to what science can currently measure.'
    },
    {
      title: 'Excellence',
      description: 'The highest global standards of competence, ethical conduct, and professional development.'
    },
    {
      title: 'Client Safety',
      description: 'Client wellbeing, dignity, and informed consent above all else. Non-negotiable.'
    },
    {
      title: 'Inclusion',
      description: 'High standards are not the exclusive property of the wealthy or the Western-educated.'
    },
    {
      title: 'Continuous Evolution',
      description: 'Standards reviewed annually. Evidence classifications updated when warranted. New modalities added as the field grows.'
    }
  ];

  return (
    <div>
      <Hero
        title="Born to Solve Two Problems at Once"
        subtitle="The Global Standard for Consciousness-Based Coaching"
      />

      {/* Our Story */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-[#1e3a5f] mb-8 text-center">Our Story</h2>
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
            <p className="text-xl leading-relaxed">
              The coaching profession had a credibility gap: coaches making unsupported claims were damaging the field 
              and failing clients. And it had an integrity gap: coaches working in profound contemplative traditions were 
              being asked to either overclaim the science or accept that their work didn't count.
            </p>
            <p className="text-xl leading-relaxed">
              ICCAC was founded to solve both simultaneously, through a framework honest enough to hold both clinically 
              validated mindfulness and centuries-old Vedantic wisdom, and rigorous enough to mean something in both cases.
            </p>
          </div>
        </div>
      </div>

      {/* Our Values */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#1e3a5f] mb-12 text-center">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-[#1e3a5f] mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Philosophy */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-[#1e3a5f] mb-8 text-center">Our Philosophy</h2>
            <h3 className="text-2xl font-semibold text-gray-800 mb-6 text-center">
              Why We Classify Evidence Rather Than Simply Require It
            </h3>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
              <p className="text-lg leading-relaxed">
                Most accreditation bodies respond to the evidence question in one of two ways. They either apply a blanket 
                scientific standard that excludes every coaching modality without a randomized controlled trial, which 
                excludes most of the consciousness-based field, or they apply no evidence standard at all.
              </p>
              <p className="text-lg leading-relaxed">
                ICCAC takes a third position. Evidence exists on a gradient, not as a binary. A coaching modality is not 
                simply proven or pseudoscientific. It may have gold-standard clinical validation. It may have strong emerging 
                research. It may have centuries of documented contemplative wisdom. All of these are legitimate positions on 
                the evidence gradient.
              </p>
              <p className="text-lg leading-relaxed">
                Our three-tier Evidence Classification System makes this gradient explicit. It tells coaches what they may 
                honestly claim. It tells clients what they are consenting to. And it tells the field that the way to earn 
                credibility is to represent the evidence truthfully, not to overclaim it.
              </p>
              <div className="bg-blue-50 border-l-4 border-[#1e3a5f] p-6 my-8">
                <p className="text-lg italic text-gray-800">
                  "Honesty about evidence is not a limitation. It is the foundation of a profession that clients can actually trust."
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mission and Vision */}
      <div className="bg-gradient-to-br from-[#1e3a5f] to-[#2d5380] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-3xl font-bold mb-6">Mission</h3>
              <p className="text-lg text-gray-200 leading-relaxed">
                To establish and uphold the global standard for consciousness-based coaching, ensuring every certified or 
                accredited coach possesses the scientific literacy, ethical integrity, practical skill, and depth of inner 
                development required to facilitate genuine, lasting human transformation.
              </p>
            </div>
            <div>
              <h3 className="text-3xl font-bold mb-6">Vision</h3>
              <p className="text-lg text-gray-200 leading-relaxed">
                A world where every human being has access to a qualified, conscious coach, and where consciousness-based 
                coaching is recognized globally as one of the most powerful, evidence-informed tools for human flourishing.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;